/** canvas 支持导出的图片类型，其他类型统一导出为 png */
const CANVAS_SUPPORTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

/** 把 jpg 之类的别名归一化成 canvas 认识的 MIME 类型 */
function normalizeOutputType(type?: string): string {
  const lower = (type || '').toLowerCase()
  if (lower === 'image/jpg') return 'image/jpeg'
  return CANVAS_SUPPORTED_TYPES.includes(lower) ? lower : 'image/png'
}

/** 加载图片元素，用于获取原始尺寸并绘制到 canvas */
export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('image load failed'))
    image.src = src
  })
}

/**
 * 按顺时针角度（90 的倍数）旋转图片，返回新的 File
 * @param file 原始图片文件
 * @param rotation 顺时针角度，会自动归一化到 0/90/180/270
 * @param image 已加载完成的图片元素，未传时内部重新加载
 */
export async function rotateImageFile(
  file: File,
  rotation: number,
  image?: HTMLImageElement | null
): Promise<File> {
  const deg = ((rotation % 360) + 360) % 360
  // 未旋转时直接返回原文件，避免无意义的重新编码
  if (deg === 0) return file

  let source = image
  let objectUrl = ''
  if (!source?.complete || !source.naturalWidth) {
    objectUrl = URL.createObjectURL(file)
    source = await loadImageElement(objectUrl)
  }

  try {
    const { naturalWidth: width, naturalHeight: height } = source
    const swapped = deg % 180 !== 0
    const canvas = document.createElement('canvas')
    canvas.width = swapped ? height : width
    canvas.height = swapped ? width : height

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas context unavailable')

    const outputType = normalizeOutputType(file.type)
    // jpeg 不支持透明通道，先铺白底避免透明区域变黑
    if (outputType === 'image/jpeg') {
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((deg * Math.PI) / 180)
    ctx.drawImage(source, -width / 2, -height / 2, width, height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), outputType, 0.92)
    })
    if (!blob) throw new Error('canvas toBlob failed')

    return new File([blob], file.name, { type: outputType, lastModified: Date.now() })
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  }
}
